// ============================================================
// bankVerification.jsx
// Common bank-account verification for the entire project.
//
// Exports:
//   • verifyBankAccount({...})     — service (API + toasts)
//   • showBankVerifyPicker({...})  — Bank 1 / Bank 2 modal
//   • useBankVerification()        — hook with in-flight state
// ============================================================
import { useState, useCallback } from 'react';
import { Modal } from 'antd';
import { toast } from 'react-toastify';
import { postRequest } from '../Utils/AppApi';
import { refId } from '../Utils/function';

const DEFAULT_PRIMARY = '#C9262C';

// ------------------------------------------------------------
// 1) SERVICE — one place to hit the API, one place for toasts
// ------------------------------------------------------------
export async function verifyBankAccount({
    accountNumber,
    ifscCode,
    recheck = false,
    endpoint,
    history,
    latitude = '0',
    longitude = '0',
    externalRef,
}) {
    if (!endpoint) {
        toast.error('Verification endpoint not configured.');
        return null;
    }
    if (!accountNumber || !ifscCode) {
        toast.error('Account number and IFSC are required before verifying.');
        return null;
    }

    const payload = {
        ifsccode: ifscCode,
        accountNumber,
        externalRef: externalRef || refId(13),
        consent: 'Y',
        pennyDrop: 'AUTO',
        recheck: !!recheck,
        latitude: latitude || '0',
        longitude: longitude || '0',
    };

    try {
        const response = await postRequest(endpoint, payload, history);

        // ---- Request-level failure (network / server) ----
        if (!response || response.success === false) {
            const msg = response?.message
                || 'Bank verification failed. Please check the account details and try again.';
            toast.error(msg);
            return null;
        }

        // ---- Extract the interesting bits from the response ----
        const dataNode = response?.data || {};
        const innerData = dataNode?.data || {};

        const name = innerData?.nameAtBank
            || innerData?.name
            || innerData?.beneficiaryName
            || dataNode?.nameAtBank
            || dataNode?.name
            || dataNode?.beneficiaryName
            || '';

        const accountStatus = String(
            dataNode?.accountStatus || innerData?.accountStatus || ''
        ).toUpperCase();

        const accountStatusCode = String(
            dataNode?.accountStatusCode || innerData?.accountStatusCode || ''
        ).toUpperCase();

        const isExplicitlyInvalid =
            accountStatus === 'INVALID' ||
            accountStatusCode.includes('INVALID');

        // ---- Verification-level failure (backend says INVALID) ----
        if (isExplicitlyInvalid || !name) {
            const msg = dataNode?.message
                || innerData?.message
                || response?.message
                || 'Bank verification failed. Please check the account details and try again.';
            toast.error(msg);
            return null;
        }

        // ---- Success ----
        // toast.success(`Verified: ${name}`);
        // return { nameAtBank: name, raw: innerData || dataNode };

        toast.success(`Verified: ${name}`);
        return { nameAtBank: name, raw: innerData || dataNode, dataNode };

    } catch (err) {
        toast.error(err?.message || 'Network error while verifying account. Please try again.');
        return null;
    }
}

// ------------------------------------------------------------
// 2) PICKER — Bank 1 / Bank 2 modal (self-contained)
// ------------------------------------------------------------
export function showBankVerifyPicker({
    onBank1,
    onBank2,
    title = 'Verify Bank Account',
    subtitle = 'Choose a verification method',
    bank1Label = 'Bank 1',
    bank1Hint = '',
    bank2Label = 'Bank 2',
    bank2Hint = '',
    primaryColor = DEFAULT_PRIMARY,
}) {
    const modal = Modal.info({
        title: null,
        icon: null,
        width: 460,
        centered: true,
        closable: true,
        maskClosable: false,
        footer: null,
        zIndex: 1050,
        className: 'bank-verify-picker',
        content: (
            <div style={{ padding: '8px 4px' }}>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <div style={{
                        width: 56, height: 56, margin: '0 auto 12px',
                        borderRadius: '50%', background: '#FEF3C7',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <i className="fa fa-university" style={{ fontSize: 26, color: '#D97706' }} />
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#1A1A2E', marginBottom: 4 }}>
                        {title}
                    </div>
                    <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>
                        {subtitle}
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                    <button
                        type="button"
                        onClick={() => { modal.destroy(); onBank1 && onBank1(); }}
                        style={{
                            flex: 1, padding: '16px 12px',
                            background: `linear-gradient(135deg, ${primaryColor}, #E8454B)`,
                            color: '#fff', border: 'none', borderRadius: 12,
                            cursor: 'pointer', textAlign: 'center',
                            boxShadow: '0 4px 12px rgba(201,38,44,0.25)',
                        }}
                    >
                        <div style={{ fontSize: 22, marginBottom: 4 }}>🏦</div>
                        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>{bank1Label}</div>
                        <div style={{ fontSize: 11, opacity: 0.85 }}>{bank1Hint}</div>
                    </button>
                    <button
                        type="button"
                        onClick={() => { modal.destroy(); onBank2 && onBank2(); }}
                        style={{
                            flex: 1, padding: '16px 12px',
                            background: '#fff', color: '#1A1A2E',
                            border: `2px solid ${primaryColor}`,
                            borderRadius: 12, cursor: 'pointer', textAlign: 'center',
                        }}
                    >
                        <div style={{ fontSize: 22, marginBottom: 4 }}>🔁</div>
                        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2, color: primaryColor }}>
                            {bank2Label}
                        </div>
                        <div style={{ fontSize: 11, color: '#6B7280' }}>{bank2Hint}</div>
                    </button>
                </div>

                <div style={{
                    marginTop: 16, padding: '10px 12px',
                    background: '#F0FDF4', borderRadius: 8,
                    fontSize: 12, color: '#166534',
                    display: 'flex', alignItems: 'flex-start', gap: 6,
                }}>
                    {/* <i className="fa fa-info-circle" style={{ marginTop: 2 }} /> */}
                    {/* <span>{bank2Label} performs a recheck. Use it if the account was verified before.</span> */}
                </div>
            </div>
        ),
    });
}

// ------------------------------------------------------------
// 3) HOOK — in-flight state, wraps the service
// ------------------------------------------------------------
export function useBankVerification() {
    const [verifyingKey, setVerifyingKey] = useState(null);

    const verify = useCallback(async ({
        key = 'default',
        onSuccess,
        onError,
        ...rest
    }) => {
        setVerifyingKey(key);
        try {
            const result = await verifyBankAccount(rest);
            if (result) onSuccess && onSuccess(result);
            else onError && onError();
            return result;
        } catch (err) {
            onError && onError(err);
            return null;
        } finally {
            setVerifyingKey(null);
        }
    }, []);

    return { verifyingKey, verify };
}